import React, { useCallback, useEffect, useState } from 'react';
import { Inbox } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { MemberPreferences } from '../../types/aai';
import {
  Chip, Divided, ErrorNote, Field, FieldGrid, ListRow, Loading, Panel, SaveBar, Select,
  SettingRow, Toggle,
} from '../aa';

/*
 * The employee's own settings: General and Notifications.
 *
 * These are the only two panels in the modal where an employee changes
 * something. Everything else in their settings is organisation policy, shown
 * read-only by EmployeeBoundary.
 *
 * The Notifications panel also carries the way into the notification inbox.
 * The topbar bell is gone, and a preferences screen that lets you choose which
 * notifications to receive while offering no way to read them would be a dead
 * end.
 *
 * Save is disabled until something actually changes, and Revert restores the
 * last saved state rather than the defaults, so neither button ever silently
 * does nothing.
 */

const START_PAGES = [
  { value: 'home', label: 'Home' },
  { value: 'my-projects', label: 'Projects' },
  { value: 'ai-adoption', label: 'AI Adoption' },
  { value: 'results', label: 'Results' },
];

const DENSITIES = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

const LANGUAGES = [{ value: 'en', label: 'English' }];

const EMAILED_KINDS = 'Session invites and schedules, information requests, connection requests and Orbit hand-offs';

// The browser knows the person's zone; offering it alongside a short list beats
// a 400-entry dropdown for a setting almost nobody changes twice.
const timezoneOptions = (current: string): string[] => {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const base = ['UTC', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'Asia/Karachi', 'Asia/Dubai', 'Asia/Singapore'];
  return Array.from(new Set([detected, current, ...base].filter(Boolean)));
};

export type PreferencesTab = 'general' | 'notifications';

interface Props {
  tab: PreferencesTab;
}

// The unread count, read where it is shown. It used to live in the shell to
// feed the topbar bell; nothing else needed it, so it moved here with the link.
const UnreadChip: React.FC = () => {
  const [unread, setUnread] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    aaiApi.notificationsSummary()
      .then(({ data }) => { if (active) setUnread(data.unread_count); })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);
  if (unread === null) return null;
  if (unread === 0) return <Chip>All read</Chip>;
  return <Chip tone="warn">{unread} unread</Chip>;
};

export const EmployeePreferences: React.FC<Props> = ({ tab }) => {
  const [saved, setSaved] = useState<MemberPreferences | null>(null);
  const [draft, setDraft] = useState<MemberPreferences | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    aaiApi.getMyPreferences()
      .then(({ data }) => {
        if (!active) return;
        setSaved(data.preferences);
        setDraft(data.preferences);
      })
      .catch(() => { if (active) setError('Could not load your settings.'); });
    return () => { active = false; };
  }, []);

  const save = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const { data } = await aaiApi.saveMyPreferences(draft);
      setSaved(data);
      setDraft(data);
      setError(null);
    } catch {
      setError('Could not save your settings. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  }, [draft]);

  if (error && !draft) return <ErrorNote message={error} />;
  if (!draft || !saved) return <Loading label="Loading your settings" />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const revert = () => setDraft(saved);
  const patch = (next: Partial<MemberPreferences>) => setDraft({ ...draft, ...next });

  if (tab === 'notifications') {
    return (
      <div className="mx-auto max-w-[760px]">
        {error && <ErrorNote className="mb-4" message={error} />}
        <Panel className="mb-5">
          <ListRow
            mark={<span className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-canvas-inset text-ink-muted"><Inbox size={15} /></span>}
            title="Your notifications"
            foot="Decisions, results and releases sent to you"
            right={<UnreadChip />}
            arrow
            to="/notifications"
          />
        </Panel>
        <Panel>
          <Divided>
            <SettingRow
              title="In the app"
              desc="Every notification appears in Your notifications as soon as it happens."
              control={<Chip>Always on</Chip>}
            />
            <SettingRow
              title="Also send these by email"
              desc={`${EMAILED_KINDS}, emailed as they happen. No daily or weekly digests.`}
              control={(
                <Toggle
                  label="Also send these by email"
                  checked={draft.notify.email_copies}
                  onChange={(value) => patch({ notify: { ...draft.notify, email_copies: value } })}
                />
              )}
            />
          </Divided>
        </Panel>
        <SaveBar onSave={save} onRevert={revert} saveLabel="Save notifications" dirty={dirty} busy={busy} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[760px]">
      {error && <ErrorNote className="mb-4" message={error} />}
      <FieldGrid>
        <Field label="Start page" htmlFor="pref-start">
          <Select id="pref-start" value={draft.start_page} onChange={(e) => patch({ start_page: e.target.value })}>
            {START_PAGES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
        <Field label="Interface density" htmlFor="pref-density">
          <Select id="pref-density" value={draft.density} onChange={(e) => patch({ density: e.target.value })}>
            {DENSITIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
        <Field label="Language" htmlFor="pref-language">
          <Select id="pref-language" value={draft.language} onChange={(e) => patch({ language: e.target.value })}>
            {LANGUAGES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
        <Field label="Time zone" htmlFor="pref-timezone">
          <Select id="pref-timezone" value={draft.timezone} onChange={(e) => patch({ timezone: e.target.value })}>
            {timezoneOptions(draft.timezone).map((z) => <option key={z} value={z}>{z}</option>)}
          </Select>
        </Field>
      </FieldGrid>
      <SaveBar onSave={save} onRevert={revert} saveLabel="Save preferences" dirty={dirty} busy={busy} />
    </div>
  );
};

export default EmployeePreferences;
